import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";

import Spinner from "@components/Spinner";
import { fetchInvoiceDetailReport } from "@service/arInvoiceApiService";

interface DetailRow {
  plt: string;
  contractor_id: string | number;
  name: string;
  reg_hours: string;
  ot_hours: string;
  pto: string;
  holiday: string;
  rate: string;
  ot_rate: string;
  other_additions: string;
  total: string;
}

interface DetailReport {
  inv_number: string;
  // Plant abbreviation — drives the saved PDF filename.
  abbreviation?: string;
  status?: string;
  voided?: boolean;
  invoice_date: string;
  invoice_due: string;
  rows: DetailRow[];
  sub: string;
  pre_admin: string;
  admin: string;
  misc: string;
  misc_items?: { description: string; amount: string }[];
  total: string;
  total_hrs: string;
}

const PRINT_ROOT_ID = "ar-invoice-detail-print-root";
const PRINT_STYLE_ID = "ar-invoice-detail-print-style";

const PURPLE = "#5b2a86";
const YELLOW = "#ffff00";

const COLUMNS = [
  "Contractor ID",
  "Name",
  "REGHRS",
  "OTHRS",
  "PTO",
  "HOLIDAY",
  "RATE",
  "OTRATE",
  "OTHER ADD",
  "TOTAL",
];

export function InvoiceDetailReportModal({
  invoiceId,
  onClose,
}: {
  invoiceId: number;
  onClose: () => void;
}) {
  const [data, setData] = useState<DetailReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isVoided =
    !!data &&
    (data.voided === true || (data.status || "").toLowerCase() === "voided");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchInvoiceDetailReport(invoiceId)
      .then((res) => {
        if (!cancelled) setData(res.data as DetailReport);
      })
      .catch((err: any) => {
        if (!cancelled)
          setError(err?.response?.data?.error || "Failed to load report");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [invoiceId]);

  const handlePrint = () => {
    const style = document.createElement("style");
    style.id = PRINT_STYLE_ID;
    style.textContent = `
      @page { size: letter landscape; margin: 0.4in 0.4in 0 0.4in; }
      @media print {
        html, body { margin: 0 !important; padding: 0 !important; height: auto !important; }
        body * { visibility: hidden; }
        #${PRINT_ROOT_ID}, #${PRINT_ROOT_ID} * { visibility: visible; }
        #${PRINT_ROOT_ID} {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          /* The @page margin already provides the page inset; the on-screen
             padding here would stack on top of it and push content onto a
             blank second page. */
          padding: 0 !important;
          overflow: hidden;
        }
        /* The 11-column table is wider than the printable area with its
           on-screen nowrap cells, so force it to fit the page width: fixed
           layout shares the width across columns and long text (names) wraps
           instead of overflowing/clipping off the right edge. */
        #${PRINT_ROOT_ID} table { width: 100% !important; table-layout: fixed; }
        #${PRINT_ROOT_ID} th, #${PRINT_ROOT_ID} td {
          white-space: normal !important;
          overflow-wrap: anywhere;
          font-size: 9px !important;
          padding: 1px 3px !important;
        }
      }
    `;
    document.head.appendChild(style);

    // The browser's "Save as PDF" suggests a filename based on document.title,
    // so swap it for the duration of the print and restore it afterwards.
    const prevTitle = document.title;
    const fileName = ["Invoice", data?.abbreviation, data?.inv_number, "Detail"]
      .filter(Boolean)
      .join("_")
      .replace(/[\\/:*?"<>|]+/g, "-");
    if (fileName) document.title = fileName;

    const cleanup = () => {
      document.getElementById(PRINT_STYLE_ID)?.remove();
      document.title = prevTitle;
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    window.print();
  };

  // A summary row spanning the labelled cell + value in the TOTAL column.
  const summaryRow = (
    label: string,
    value: string,
    bg?: string,
    bold = false,
    key?: string | number
  ) => (
    <tr key={key}>
      <td
        style={{
          ...cell,
          background: bg,
          fontWeight: bold ? "bold" : "normal",
        }}
      >
        {label}
      </td>
      {Array.from({ length: 8 }).map((_, i) => (
        <td key={i} style={{ ...cell, background: bg }} />
      ))}
      <td
        style={{
          ...cell,
          textAlign: "right",
          background: bg,
          fontWeight: bold ? "bold" : "normal",
        }}
      >
        ${value}
      </td>
    </tr>
  );

  // A miscellaneous line: "Misc" in the Contractor ID column, the record's
  // description in the Name column, and the amount in the TOTAL column.
  const miscRow = (
    description: string,
    value: string,
    key?: string | number
  ) => (
    <tr key={key}>
      <td style={{ ...cell, background: YELLOW }}>Misc</td>
      <td style={{ ...cell, background: YELLOW }}>{description}</td>
      {Array.from({ length: 7 }).map((_, i) => (
        <td key={i} style={{ ...cell, background: YELLOW }} />
      ))}
      <td style={{ ...cell, textAlign: "right", background: YELLOW }}>
        ${value}
      </td>
    </tr>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: -4, right: 6, zIndex: 2 }}
        >
          <CloseIcon />
        </IconButton>

        {loading && (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20">
            <Spinner className="tw-w-8 tw-h-8" />
            <span className="tw-mt-2 tw-text-sm">Loading report...</span>
          </div>
        )}

        {!loading && error && (
          <div className="tw-text-center tw-py-10 tw-text-red-600">{error}</div>
        )}

        {!loading && data && (
          <div
            id={PRINT_ROOT_ID}
            style={{
              position: "relative",
              fontFamily: "Arial, Helvetica, sans-serif",
              fontSize: 11,
              color: "#000",
              padding: "24px 28px",
            }}
          >
            {isVoided && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              >
                <div
                  style={{
                    border: "8px solid rgba(184, 28, 28, 0.35)",
                    color: "rgba(184, 28, 28, 0.35)",
                    fontSize: 64,
                    fontWeight: 800,
                    letterSpacing: 10,
                    padding: "14px 42px",
                    transform: "rotate(-24deg)",
                    userSelect: "none",
                    textTransform: "uppercase",
                  }}
                >
                  Voided
                </div>
              </div>
            )}

            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div style={{ lineHeight: 1.5 }}>
                <div>Invoice #&nbsp;&nbsp;{data.inv_number}</div>
                <div>Invoice Date&nbsp;&nbsp;{data.invoice_date}</div>
                <div>Invoice Due&nbsp;&nbsp;{data.invoice_due}</div>
              </div>
              <div style={{ fontSize: 16, fontWeight: "bold" }}>
                Buildings And Builders
              </div>
              <div
                style={{
                  width: 120,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <img
                  src="/logo.png"
                  alt="Buildings & Builders"
                  style={{ height: 44, objectFit: "contain" }}
                />
              </div>
            </div>

            {/* Table */}
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: 12,
              }}
            >
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th
                      key={c}
                      style={
                        // Give the contractor Name column double the width of
                        // the others (fixed print layout splits the rest evenly).
                        c === "Name" ? { ...headCell, width: "18%" } : headCell
                      }
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, idx) => (
                  <tr key={idx}>
                    <td style={cell}>{r.contractor_id}</td>
                    <td style={cell}>{r.name}</td>
                    <td style={numCell}>{r.reg_hours}</td>
                    <td style={numCell}>{r.ot_hours}</td>
                    <td style={numCell}>${r.pto}</td>
                    <td style={numCell}>${r.holiday}</td>
                    <td style={numCell}>${r.rate}</td>
                    <td style={numCell}>${r.ot_rate}</td>
                    <td style={numCell}>${r.other_additions}</td>
                    <td style={numCell}>${r.total}</td>
                  </tr>
                ))}

                {summaryRow("SUB", data.sub)}
                {summaryRow("Pre Admin", data.pre_admin)}
                {summaryRow("ADMIN", data.admin, YELLOW)}
                {/* One line per miscellaneous record: "Misc" in Contractor ID,
                    description in Name. When there are none, a single "Misc"
                    line with no description at zero. */}
                {data.misc_items && data.misc_items.length > 0
                  ? data.misc_items.map((m, i) =>
                      miscRow(m.description, m.amount, `misc-${i}`)
                    )
                  : miscRow("", "0.00")}

                {/* TOTAL row (purple), HRS under the hours columns */}
                <tr>
                  <td
                    style={{
                      ...cell,
                      background: PURPLE,
                      color: "#fff",
                      fontWeight: "bold",
                    }}
                  >
                    TOTAL
                  </td>
                  <td
                    style={{
                      ...cell,
                      background: PURPLE,
                      color: "#fff",
                      textAlign: "right",
                    }}
                  >
                    {data.total_hrs} HRS
                  </td>
                  {Array.from({ length: 7 }).map((_, i) => (
                    <td key={i} style={{ ...cell, background: PURPLE }} />
                  ))}
                  <td
                    style={{
                      ...cell,
                      background: PURPLE,
                      color: "#fff",
                      textAlign: "right",
                      fontWeight: "bold",
                    }}
                  >
                    ${data.total}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
        <Button
          variant="contained"
          sx={{ bgcolor: "#2642c9", textTransform: "none" }}
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={loading || !data}
        >
          Print / Save as PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const cell: React.CSSProperties = {
  border: "1px solid #000",
  padding: "2px 6px",
  height: 18,
  fontSize: 12,
  whiteSpace: "nowrap",
};

const numCell: React.CSSProperties = { ...cell, textAlign: "right" };

const headCell: React.CSSProperties = {
  ...cell,
  fontSize: 12,
  fontWeight: "normal",
  textAlign: "left",
  background: "#999999",
  color: "#fff",
};

export default InvoiceDetailReportModal;
